/**
 * The kit's icon set: 16 px duotone line icons (ink stroke over a neutral tone; status icons in the
 * success, warning, destructive and brand families only). Line art comes from
 * Hugeicons' free set (MIT, `@hugeicons/core-free-icons`): a recipe names its source icon (`from`) and
 * paints its elements by index (`use`) on that icon's 24 unit grid, with stroke widths in grid units.
 * Tones, washes and the few shapes the free set lacks are drawn here from circles, rectangles, lines
 * and arcs, on the recipe's grid.
 * `nav-*` (the sidebar's) and `table-search` (a table toolbar's) name the places an icon is used, so
 * one can change without the other. Graphics are the non-square bits of line art.
 */
import {
  Alert01Icon,
  Analytics01Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  ArrowUpDownIcon,
  Book02Icon,
  Calendar01Icon,
  CaptionsIcon,
  Clock01Icon,
  Clock04Icon,
  CommandIcon,
  Comment01Icon,
  DashboardSquare01Icon,
  File02Icon,
  FilterIcon,
  FlowConnectionIcon,
  HeadsetIcon,
  MoreVerticalCircle01Icon,
  News01Icon,
  Notification01Icon,
  RepeatIcon,
  Search01Icon,
  Settings03Icon,
  SidebarRightIcon,
  StarIcon,
  Target01Icon,
  Ticket02Icon,
  Tick02Icon,
  UserRoundIcon,
  ZapIcon,
} from "@hugeicons/core-free-icons";
import {
  BLACK,
  BORDER,
  BRAND,
  BRAND_TINT_STRONG,
  CHART_ACTIVE,
  CHART_GRID,
  DESTRUCTIVE,
  DESTRUCTIVE_TINT,
  FOREGROUND,
  INFO,
  INFO_TINT,
  INK,
  INNER,
  KBD,
  MUTED,
  MUTED_INK,
  PAPER,
  SUBTLE,
  SUCCESS,
  SUCCESS_TINT,
  TONE,
  WARNING,
  WARNING_TINT,
  WHITE,
  type Colour,
} from "./palette";
import type { Recipe } from "./paint";

/** The ticket's stub: Ticket02's outline from one end of its tear line round to the other, closed along the line. */
const TICKET_STUB =
  "M19.3 15.3L18.6434 16.1051L16.1051 18.6434C14.0241 20.7244 12.9836 21.7649 11.6994 21.9557C11.2242 22.0263 10.7357 22.0124 10.2557 21.9146C9.4216 21.7446 8.66841 21.2285 7.65127 20.296C7.47734 20.1366 7.49342 19.8655 7.65074 19.7082C8.52693 18.832 8.48529 17.3698 7.55776 16.4422C6.63022 15.5147 5.16802 15.4731 4.29183 16.3493C4.13451 16.5066 3.86341 16.5227 3.70395 16.3487C2.77146 15.3316 2.25536 14.5784 2.08539 13.7443C1.98759 13.2643 1.97368 12.7758 2.04429 12.3006C2.23513 11.0164 3.27561 9.97588 5.35657 7.89492L7.89492 5.35657L8.7 4.7Z";

const ticket = (tone: Colour, ink: Colour): Recipe => ({ w: 16, h: 16, from: Ticket02Icon, layers: [
  { d: TICKET_STUB, fill: tone },
  { use: 0, stroke: ink, join: "round" },
  { use: 1, stroke: ink, cap: "round", join: "round" },
] });

const news: Recipe = { w: 16, h: 16, from: News01Icon, layers: [
  { use: 1, stroke: INK, cap: "round", join: "round" },
  { use: 2, fill: TONE, stroke: INK, cap: "round", join: "round" },
  { use: 0, stroke: INK, cap: "round", join: "round" },
] };

const dashboard: Recipe = { w: 16, h: 16, from: DashboardSquare01Icon, layers: [0, 1, 2, 3].map((use) => ({ use, fill: TONE, stroke: INK, width: 1.5, cap: "square" as const, join: "round" as const })) };

const settings: Recipe = { w: 16, h: 16, from: Settings03Icon, layers: [
  { use: 1, fill: TONE, stroke: INK, width: 1.8 },
  { use: 0, fill: INNER, stroke: INK, width: 1.8 },
] };

const search: Recipe = { w: 16, h: 16, from: Search01Icon, layers: [
  { use: 0, stroke: INK, width: 1.8, cap: "round", join: "round" },
  { use: 1, stroke: INK, width: 1.8, cap: "round", join: "round" },
] };

/** A bar from Hugeicons' Chart01: 3 units wide with its smooth corners, standing on y 19 from `top`. */
const bar = (x: number, top: number) =>
  `M${x} ${top + 1.5}c0-.46594 0-.69891.07612-.88268c.10149-.24503.29617-.43971.5412-.5412c.18377-.07612.41674-.07612.88268-.07612` +
  `c.46594 0 .69891 0 .88268.07612c.24503.10149.43971.29617.5412.5412c.07612.18377.07612.41674.07612.88268V17.5` +
  `c0 .46594 0 .69891-.07612.88268c-.10149.24503-.29617.43971-.5412.5412c-.18377.07612-.41674.07612-.88268.07612` +
  `c-.46594 0-.69891 0-.88268-.07612c-.24503-.10149-.43971-.29617-.5412-.5412c-.07612-.18377-.07612-.41674-.07612-.88268Z`;

/** Signal bars, short to tall, as [colour, opacity] (opacity 1 unless given). */
type Bar = [colour: Colour, opacity?: number];
const signal = (bars: [Bar, Bar, Bar]): Recipe => ({ w: 16, h: 16, layers: [
  { d: bar(16, 5), fill: bars[2][0], fillOpacity: bars[2][1] },
  { d: bar(10.75, 8), fill: bars[1][0], fillOpacity: bars[1][1] },
  { d: bar(5.5, 12), fill: bars[0][0], fillOpacity: bars[0][1] },
] });

const file = (tone: Colour, ink: Colour, width?: number): Recipe => ({ w: 16, h: 16, from: File02Icon, layers: [
  { use: 2, fill: tone },
  { use: 2, stroke: ink, width, cap: "round", join: "round" },
  { use: 0, stroke: ink, width, cap: "round", join: "round" },
  { use: 1, stroke: ink, width, cap: "round", join: "round" },
] });

const star = (tone: Colour, ink: Colour, width?: number): Recipe => ({ w: 16, h: 16, from: StarIcon, layers: [
  { use: 0, fill: tone, stroke: ink, width, cap: "round", join: "round" },
] });

export const ICONS = {
  analytics: { w: 16, h: 16, from: Analytics01Icon, layers: [
    { use: 3, fill: TONE, stroke: INK, width: 1.5, join: "round" },
    { use: 0, stroke: INK, width: 1.5, cap: "round" },
    { use: 1, stroke: INK, width: 1.5, cap: "round" },
    { use: 2, stroke: INK, width: 1.5, cap: "round" },
  ] },
  bell: { w: 16, h: 16, from: Notification01Icon, layers: [
    { use: 0, stroke: INK, width: 1.8, cap: "round", join: "round" },
    { use: 1, fill: TONE, stroke: INK, width: 1.8, cap: "round", join: "round" },
  ] },
  calendar: { w: 16, h: 16, from: Calendar01Icon, layers: [
    { d: "M3 10C3.05 7.52 3.23 6.11 4.17 5.17C5.34 4 7.23 4 11 4H13C16.77 4 18.66 4 19.83 5.17C20.77 6.11 20.95 7.52 21 10Z", fill: TONE },
    { use: 0, stroke: INK, width: 1.8, cap: "round", join: "round" },
    { use: 1, stroke: INK, width: 1.8, cap: "round", join: "round" },
    { use: 2, stroke: INK, width: 1.8, cap: "round", join: "round" },
    { d: "M12 14H12.01M12 18H12.01M16 14H16.01M8 14H8.01M8 18H8.01", stroke: INK, width: 2.1, cap: "round", join: "round" },
  ] },
  check: { w: 10, h: 10, from: Tick02Icon, layers: [
    { use: 0, stroke: WHITE, width: 2.88, cap: "round", join: "round" },
  ] },
  "chevron-down": { w: 12, h: 12, from: ArrowDown01Icon, layers: [
    { use: 0, stroke: SUBTLE, width: 2.4, cap: "round", join: "round" },
  ] },
  "chevron-up": { w: 12, h: 12, from: ArrowUp01Icon, layers: [
    { use: 0, stroke: SUBTLE, width: 2.4, cap: "round", join: "round" },
  ] },
  clock: { w: 16, h: 16, from: Clock01Icon, layers: [
    { use: 0, fill: TONE, stroke: INK, width: 1.5 },
    { use: 1, stroke: INK, width: 1.5, cap: "round", join: "round" },
  ] },
  command: { w: 12, h: 12, from: CommandIcon, layers: [0, 1, 2, 3, 4].map((use) => ({ use, stroke: KBD, cap: "round" as const, join: "round" as const })) },
  comment: { w: 16, h: 16, from: Comment01Icon, layers: [
    { use: 1, fill: TONE, stroke: INK, width: 1.8, cap: "round" },
    { use: 0, stroke: INK, width: 1.8, cap: "round", join: "round" },
  ] },
  dashboard,
  "feed-alert": { w: 16, h: 16, from: Alert01Icon, layers: [
    { use: 0, fill: DESTRUCTIVE_TINT, stroke: DESTRUCTIVE, cap: "round", join: "round" },
    { d: "M12 9V13.5", stroke: DESTRUCTIVE, cap: "round", join: "round" },
    { d: "M12 17V17.01", stroke: DESTRUCTIVE, width: 1.8, cap: "round", join: "round" },
  ] },
  "feed-book": { w: 16, h: 16, from: Book02Icon, layers: [
    { d: "M4 20C4 18.8954 4.89543 18 6 18H20V6C20 4.11438 20 3.17157 19.4142 2.58579C18.8284 2 17.8856 2 16 2H10C7.17157 2 5.75736 2 4.87868 2.87868C4 3.75736 4 5.17157 4 8V20Z", fill: INFO_TINT, stroke: INFO, cap: "round", join: "round" },
    { use: 1, stroke: INFO, cap: "round", join: "round" },
    { use: 0, stroke: INFO, cap: "round", join: "round" },
    { use: 2, stroke: INFO, cap: "round", join: "round" },
  ] },
  "feed-repeat": { w: 16, h: 16, from: RepeatIcon, layers: [
    { rect: [1.5, 4.5, 21, 15, 7.5], fill: INFO_TINT },
    { use: 0, stroke: INFO, cap: "round", join: "round" },
    { use: 1, stroke: INFO, cap: "round", join: "round" },
  ] },
  "feed-star": star(WARNING_TINT, WARNING),
  "feed-ticket": ticket(INFO_TINT, INFO),
  "feed-user": { w: 16, h: 16, from: UserRoundIcon, layers: [
    { d: "M19.25 21C19.25 17.13 15.87 13.5 12 13.5C8.13 13.5 5.25 17.13 5.25 21Z", fill: SUCCESS_TINT },
    { use: 0, fill: SUCCESS_TINT, stroke: SUCCESS, cap: "round", join: "round" },
    { use: 1, stroke: SUCCESS, cap: "round", join: "round" },
  ] },
  file: file(TONE, INK, 1.5),
  filter: { w: 16, h: 16, from: FilterIcon, layers: [
    { use: 0, fill: TONE, stroke: INK, width: 1.5, cap: "round", join: "round" },
  ] },
  flow: { w: 16, h: 16, from: FlowConnectionIcon, layers: [
    { use: 0, fill: TONE, stroke: INK, width: 1.5 },
    { use: 1, fill: TONE, stroke: INK, width: 1.5 },
    { use: 2, stroke: INK, width: 1.5, cap: "round", join: "round" },
    { use: 3, fill: TONE, stroke: INK, width: 1.5 },
  ] },
  headset: { w: 16, h: 16, from: HeadsetIcon, layers: [
    { use: 0, fill: TONE, stroke: INK, width: 1.8 },
    { use: 1, fill: TONE, stroke: INK, width: 1.8 },
    { use: 2, stroke: INK, width: 1.8, cap: "round", join: "round" },
  ] },
  more: { w: 16, h: 16, from: MoreVerticalCircle01Icon, layers: [2, 1, 0].map((use) => ({ use, fill: INK, stroke: INK, width: 1.8 })) },
  "nav-dashboard": dashboard,
  "nav-news": news,
  "nav-settings": settings,
  "nav-ticket": ticket(TONE, INK),
  news,
  search,
  selector: { w: 18, h: 18, layers: [
    { d: "M7 9l5-5 5 5M7 15l5 5 5-5", stroke: KBD, width: 2, cap: "round", join: "round" },
  ] },
  settings,
  sidebar: { w: 16, h: 16, from: SidebarRightIcon, layers: [
    { use: 0, stroke: SUBTLE, width: 1.8 },
    { use: 1, stroke: SUBTLE, width: 1.8, join: "round" },
  ] },
  "signal-full": signal([[DESTRUCTIVE], [DESTRUCTIVE], [DESTRUCTIVE]]),
  "signal-low": signal([[WARNING], [WARNING, 0.3], [WARNING, 0.3]]),
  "signal-medium": signal([[WARNING], [WARNING], [WARNING, 0.3]]),
  sort: { w: 12, h: 12, from: ArrowUpDownIcon, layers: [0, 1, 2, 3].map((use) => ({ use, stroke: SUBTLE, width: 2.4, cap: "round" as const, join: "round" as const })) },
  star: star(TONE, INK, 1.5),
  "status-delivered": { w: 16, h: 16, from: Tick02Icon, layers: [
    { use: 0, stroke: SUCCESS, width: 1.8, cap: "round", join: "round" },
  ] },
  "status-progress": { w: 16, h: 16, from: Clock04Icon, layers: [
    { circle: [12, 11.8125, 10.5], fill: WARNING_TINT },
    { use: 0, stroke: WARNING, cap: "round", join: "round" },
    { use: 1, stroke: WARNING, cap: "round", join: "round" },
    { use: 2, stroke: WARNING, cap: "round", join: "round" },
  ] },
  "status-review": file(BRAND_TINT_STRONG, BRAND),
  "table-search": search,
  target: { w: 16, h: 16, from: Target01Icon, layers: [
    { circle: [12, 12, 10.5], fill: TONE },
    { circle: [12, 12, 4.5], fill: MUTED },
    { use: 0, stroke: INK, cap: "round" },
    { use: 1, stroke: INK, cap: "round", join: "round" },
    { use: 2, stroke: INK, cap: "round" },
  ] },
  ticket: ticket(TONE, INK),
  timer: { w: 16, h: 16, grid: [16, 16], layers: [
    { circle: [8.8, 8.4, 6], fill: TONE },
    { d: "M1.333 14.667H8.667A6 6 0 0 0 14.667 8.667A6 6 0 0 0 8.667 2.667C5.58 2.667 3 5 2.667 8", stroke: INK, width: 1.25, cap: "round" },
    { d: "M12.333 3.667L13 3M3.667 3L4.333 3.667", stroke: INK, width: 1.25, cap: "round", join: "round" },
    { d: "M11 6L9.04 7.96", stroke: INK, width: 1.25, cap: "round" },
    { circle: [8.333, 8.667, 1], stroke: INK, width: 1.25 },
    { d: "M8.333 2.333V1.333M7 1.333H9.667M1.333 10H3.333M1.333 12.667H4.667", stroke: INK, width: 1.25, cap: "round", join: "round" },
  ] },
  "user-card": { w: 16, h: 16, from: CaptionsIcon, layers: [
    { use: 0, fill: TONE, stroke: INK, width: 1.5, cap: "round", join: "round" },
    { circle: [9, 10, 2], stroke: INK, width: 1.5 },
    { d: "M13 16C13 13.79 11.21 12 9 12C6.79 12 5 13.79 5 16", stroke: INK, width: 1.5, cap: "round", join: "round" },
    { d: "M15 9H19M15 12H19", stroke: INK, width: 1.5, cap: "round", join: "round" },
  ] },
  "user-group": { w: 16, h: 16, grid: [16, 16], layers: [
    { d: "M14.667 11A3.667 3.333 0 0 0 11 7.667H5A3.667 3.333 0 0 0 1.333 11V11.5H14.667Z", fill: TONE },
    { d: "M14.667 11A3.667 3.333 0 0 0 11 7.667H5A3.667 3.333 0 0 0 1.333 11", stroke: INK, width: 1, cap: "round", join: "round" },
    { d: "M10.322 7.567A2.333 2.333 0 1 0 8.678 5.1", fill: TONE, stroke: INK, width: 1, cap: "round", join: "round" },
    { d: "M7.321 5.1A2.333 2.333 0 1 0 5.678 7.567", fill: TONE, stroke: INK, width: 1, cap: "round", join: "round" },
    { circle: [8, 7.333, 2.333], fill: INNER, stroke: INK, width: 1 },
    { d: "M11.667 13A3.667 3.333 0 0 0 8 9.667A3.667 3.333 0 0 0 4.333 13", fill: INNER },
    { d: "M11.667 13A3.667 3.333 0 0 0 8 9.667A3.667 3.333 0 0 0 4.333 13", stroke: INK, width: 1, cap: "round", join: "round" },
  ] },
  zap: { w: 16, h: 16, from: ZapIcon, layers: [
    { use: 0, fill: TONE, stroke: INK, width: 1.875, join: "round" },
  ] },
} satisfies Record<string, Recipe>;

export const GRAPHICS = {
  "chart-grid": { w: 825, h: 216, stretch: true, grid: [825, 216], layers: [
    { d: "M0 .4h825M0 53.9h825M0 107.4h825M0 160.9h825M0 214.9h825", stroke: CHART_GRID, width: 0.8 },
  ] },
  dot: { w: 4, h: 4, grid: [4, 4], layers: [
    { circle: [2, 2, 1.5], fill: FOREGROUND, stroke: FOREGROUND, width: 1 },
  ] },
  "online-dot": { w: 9, h: 9, grid: [9, 9], layers: [
    { circle: [4.5, 4.5, 4.1], fill: SUCCESS, stroke: WHITE, width: 0.8 },
  ] },
  "rule-dark": { w: 560, h: 1, stretch: true, grid: [560, 1], layers: [
    { d: "M.4 .4h559", stroke: FOREGROUND, width: 0.8, cap: "round", dash: "4 4" },
  ] },
  "rule-feed": { w: 296, h: 1, stretch: true, grid: [296, 1], layers: [
    { d: "M0 .5h296", stroke: BORDER, width: 1, dash: "4 4" },
  ] },
  /* Stretched to the sidebar's inner width, whatever `--spacing-sidebar` makes it (like `rule-feed`). */
  "rule-sidebar": { w: 240, h: 1, stretch: true, grid: [240, 1], layers: [
    { d: "M.5 .5h239", stroke: BLACK, width: 1, strokeOpacity: 0.1, cap: "round", dash: "4 4" },
  ] },
  slash: { w: 7, h: 11, grid: [7, 11], layers: [
    { d: "M.429 10.257l6-10", stroke: MUTED_INK, width: 1 },
  ] },
  /* A tag: a 60 px body with 6 px corners, pointed at x 75 through 8 px shoulders and a 4 px tip; the border sits 0.5 px inside. */
  "tooltip-tag": { w: 73, h: 24, grid: [73, 24], layers: [
    { d: "M6 0H57.194A8 8 0 0 1 62.191 1.753L71.096 8.877A4 4 0 0 1 71.096 15.123L62.191 22.247A8 8 0 0 1 57.194 24H6A6 6 0 0 1 0 18V6A6 6 0 0 1 6 0Z", fill: CHART_ACTIVE },
    { d: "M6 .5H57.194A7.5 7.5 0 0 1 61.879 2.143L70.783 9.267A3.5 3.5 0 0 1 70.783 14.733L61.879 21.857A7.5 7.5 0 0 1 57.194 23.5H6A5.5 5.5 0 0 1 .5 18V6A5.5 5.5 0 0 1 6 .5Z", stroke: WHITE, strokeOpacity: 0.1 },
  ] },
  "tree-dot": { w: 4, h: 4, grid: [4, 4], layers: [
    { circle: [2, 2, 1.6], fill: PAPER, stroke: SUBTLE, width: 0.8 },
  ] },
  "tree-line": { w: 9, h: 21, grid: [9, 21], layers: [
    { d: "M.4 .4v18a2 2 0 0 0 2 2h6", stroke: SUBTLE, width: 0.8, cap: "round" },
  ] },
} satisfies Record<string, Recipe>;
